import * as Color from '../utils/color';

const defaultColors = {
  text: '#444444',
  background: '#ffffff',
  primary: '#E94093',
  'primary-contrast': '#ffffff',
  border: '#dddddd'
};

export default class Theme {
  // retrieve the className to apply to DOM elements to scope them to this theme
  get className () {
    return `iono-player-${this.id}`;
  }

  /**
   * Constructor.
   *
   * @param  {Object} options Theme options
   */
  constructor (layout, options) {
    this.layout = layout;
    this.colors = {};
    this.rounded = 0;

    this.setRounded(options.rounded, false);
    this.setColors(options.colors, false);
  }

  /**
   * Retrieve a theme color
   *
   * @param  {string} key The theme color key
   *
   * @return {Color}
   */
  getColor (key) {
    return this.colors[key] || this.getAutoColor(key) || Color.hexToRgb(defaultColors[key]);
  }

  getAutoColor (key) {
    const backgroundContrast = Color.contrast(this.getColor('background'));
    switch (key) {
      case 'text':
        return Color.mix(this.getColor('background'), backgroundContrast, 0.265);
      case 'muted-controls':
        return Color.mix(this.getColor('text'), this.getColor('background'), 0.2);
      case 'muted':
        return Color.mix(this.getColor('text'), this.getColor('background'), 0.6);
      case 'border':
        return Color.mix(this.getColor('text'), this.getColor('background'), 0.135);
      case 'light':
        return Color.mix(this.getColor('text'), this.getColor('background'), 0.065);
      case 'primary-contrast':
        return Color.mix(Color.contrast(this.getColor('primary')), this.getColor('primary'), 0.95);
      case 'primary-muted':
        return Color.mix(this.getColor('primary'), this.getColor('background'), 0.6);
    }
  }

  /**
   * Change a theme color
   *
   * @param {String}  key    The color key to be changed
   * @param {String}  color  The new color value
   * @param {Boolean} update Update the stylesheet after changing color
   *
   * @return {self}
   */
  setColor (key, color, update = true) {
    this.colors[key] = color !== null ? Color.hexToRgb(color) : null;

    // update the theme
    if (update) {
      this.update();
    }

    return this;
  }

  /**
   * Change the theme colors
   *
   * @param {Object}  colors A mapping of color keys to a color value
   * @param {Boolean} update Update the stylesheet after changing colors
   *
   * @return {self}
   */
  setColors (colors, update = true) {
    for (const key in colors) {
      this.setColor(key, colors[key], false);
    }

    if (update) {
      this.update();
    }

    return this;
  }

  getRounded () {
    return this.rounded;
  }

  /**
   * Change the rounded option
   *
   * @param {Number} amount
   *
   * @return {self}
   */
  setRounded (amount, update = true) {
    if (typeof amount !== 'number') {
      amount = parseInt(amount, 10);
    }

    this.rounded = Math.max(0, Math.min(20, amount));

    if (update) {
      this.update();
    }

    return this;
  }

  /**
   * Render the theme stylesheet contents
   *
   * @return {self}
   */
  render () {
    if (this.layout.isAttached()) {
      const styles = this.layout.$element.style;

      styles.setProperty('--border-radius', `${this.getRounded()}px`);
      styles.setProperty('--primary', Color.rgbToHex(...this.getColor('primary')));
      styles.setProperty('--primary-contrast', Color.rgbToHex(...this.getColor('primary-contrast')));
      styles.setProperty('--primary-muted', Color.rgbToHex(...this.getColor('primary-muted')));
      styles.setProperty('--text', Color.rgbToHex(...this.getColor('text')));
      styles.setProperty('--background', Color.rgbToHex(...this.getColor('background')));
      styles.setProperty('--muted', Color.rgbToHex(...this.getColor('muted')));
      styles.setProperty('--border', Color.rgbToHex(...this.getColor('border')));
      styles.setProperty('--light', Color.rgbToHex(...this.getColor('light')));
    }
  }

  /**
   * Update the themes stylesheet
   *
   * @return {self}
   */
  update () {
    this.render();
    return this;
  }
}

// Theme.defaultOptions = {
//   rounded: 5,
//   colors: {
//     text: '#444444',
//     background: '#ffffff',
//     primary: '#E94093',
//     border: '#dddddd'
//   }
// };
